import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Check, Loader2, LogOut, MapPin, Package, TriangleAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TKEY = "fr_staff_token";
const STATUSI = ["da_fare", "in_viaggio", "completata", "problema"];

export default function DriverPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TKEY));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tasks, setTasks] = useState(null);
  const [busy, setBusy] = useState("");

  const load = (t) => {
    axios.get(`${API}/driver/today`, { headers: { Authorization: `Bearer ${t}` } }).then(({ data }) => setTasks(data.tasks)).catch((e) => {
      toast.error(e.response?.data?.detail || "Errore");
      localStorage.removeItem(TKEY);
      setToken(null);
    });
  };
  useEffect(() => { if (token) load(token); }, []); // eslint-disable-line

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem(TKEY, r.data.access_token);
      setToken(r.data.access_token);
      load(r.data.access_token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenziali non valide");
    }
  };

  const setStatus = async (id, status) => {
    setBusy(id);
    try {
      await axios.patch(`${API}/driver/deliveries/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      load(token);
    } catch (err) {
      toast.error(err.response?.data?.detail);
      if (status === "problema" && err.response?.status === 409) load(token);
    } finally { setBusy(""); }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="driver-login">
        <form onSubmit={login} className="bg-white rounded-2xl shadow-card p-6 w-full max-w-sm">
          <p className="font-extrabold text-fr-navy text-lg">Area Driver</p>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email staff" type="email" className="mt-3 w-full h-11 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="driver-login-email" />
          <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" type="password" className="mt-2 w-full h-11 rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm" data-testid="driver-login-password" />
          <button type="submit" className="mt-3 w-full h-11 rounded-xl bg-fr-navy text-white font-bold text-sm">Accedi</button>
          <a href="/" className="block mt-3 text-center text-[11px] text-gray-400 underline">← Torna al sito</a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="driver-page">
      <header className="bg-fr-navy text-white h-14 flex items-center px-4">
        <p className="font-extrabold">FabriRent Driver — oggi</p>
        <button onClick={() => { localStorage.removeItem(TKEY); setToken(null); }} className="ml-auto text-xs flex items-center gap-1 bg-white/10 rounded-lg px-3 py-2"><LogOut size={13} /> Esci</button>
      </header>
      <div className="max-w-2xl mx-auto p-4 space-y-3">
        {!tasks && <Loader2 className="animate-spin mx-auto mt-10 text-fr-navy" />}
        {tasks && tasks.length === 0 && <p className="text-sm text-gray-400">Nessuna consegna/ritiro assegnato per oggi.</p>}
        {tasks && tasks.map((t) => (
          <div key={t.id} className={`bg-white rounded-2xl border p-4 ${t.status === "completata" ? "opacity-60" : "border-gray-100 shadow-card"}`} data-testid={`driver-task-${t.id}`}>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-extrabold rounded px-1.5 py-0.5 bg-fr-ink text-white uppercase">{t.tipo}</span>
              <span className="font-mono font-bold text-fr-navy text-sm">{t.booking_reference}</span>
              <span className="ml-auto text-[11px] font-bold text-gray-500">{format(new Date(t.when), "HH:mm")}</span>
            </div>
            <p className="text-xs text-gray-600 mt-2 flex items-start gap-1.5"><MapPin size={13} className="text-fr-red shrink-0 mt-0.5" /> {t.address}</p>
            <p className="text-[11px] text-gray-400 mt-0.5">Veicolo: {t.targa || "da assegnare"} • Cliente: {t.customer?.first_name} {t.customer?.phone}</p>
            <div className="mt-2.5 grid grid-cols-2 gap-1.5">
              {STATUSI.map((st) => (
                <button key={st} onClick={() => setStatus(t.id, st)} disabled={busy === t.id || t.status === st}
                  className={`h-9 rounded-lg text-[10px] font-bold flex items-center justify-center gap-1 ${t.status === st ? "bg-green-600 text-white" : st === "problema" ? "bg-red-50 text-fr-red" : "bg-gray-100 text-gray-700"}`}
                  data-testid={`driver-task-${st}-${t.id}`}>
                  {busy === t.id ? <Loader2 size={11} className="animate-spin" /> : <>{st === "completata" && <Check size={11} />} {st.replace("_", " ")}</>}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
